import { useState } from 'react';

interface Step {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  required: boolean;
}

interface InteractiveStepsProps {
  title: string;
  steps: Step[];
  onStepsUpdate: (steps: Step[]) => void;
  onExecute: (selectedSteps: Step[]) => void;
  onPlanChange?: (planType: 'simple' | 'complex') => void;
  isExecuting?: boolean;
  isRegenerating?: boolean;
}

export const InteractiveSteps = ({
  title,
  steps,
  onStepsUpdate,
  onExecute,
  onPlanChange,
  isExecuting = false,
  isRegenerating = false
}: InteractiveStepsProps) => {
  const [localSteps, setLocalSteps] = useState<Step[]>(steps);

  const toggleStep = (stepId: string) => {
    const updatedSteps = localSteps.map(step =>
      step.id === stepId
        ? { ...step, completed: !step.completed }
        : step
    );
    setLocalSteps(updatedSteps);
    onStepsUpdate(updatedSteps);
  };

  const selectedCount = localSteps.filter(step => step.completed).length;
  const totalSteps = localSteps.length;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 my-4 max-w-md">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-white font-medium text-sm mb-2">{title}</h3>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-400">Select Steps</span>
          <span className="text-xs text-gray-400">{selectedCount}/{totalSteps} Selected</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(selectedCount / totalSteps) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Steps List */}
      <div className="space-y-2 mb-4">
        {localSteps.map((step) => (
          <label
            key={step.id}
            className="flex items-start space-x-3 p-2 rounded hover:bg-gray-700/50 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              checked={step.completed}
              onChange={() => toggleStep(step.id)}
              className="mt-0.5 rounded bg-gray-700 border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-200">{step.title}</div>
              {step.description && (
                <div className="text-xs text-gray-400 mt-1">{step.description}</div>
              )}
              {step.required && (
                <span className="inline-block text-xs bg-red-600/20 text-red-400 px-2 py-0.5 rounded mt-1">
                  Required
                </span>
              )}
            </div>
          </label>
        ))}
      </div>

      {/* Execute Button */}
      <button
        onClick={() => onExecute(localSteps.filter(step => step.completed))}
        disabled={selectedCount === 0 || isExecuting}
        className={`w-full px-4 py-2 rounded-lg font-medium text-sm transition-all ${
          selectedCount === 0 || isExecuting
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white'
        }`}
      >
        {isExecuting ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span>Executing...</span>
          </div>
        ) : (
          `✨ Perform Steps ${selectedCount > 0 ? selectedCount : ''}`
        )}
      </button>

      {/* Mode Toggles */}
      {onPlanChange && (
        <div className="flex space-x-2 mt-3">
          <button
            onClick={() => onPlanChange('simple')}
            disabled={isRegenerating}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              isRegenerating
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {isRegenerating ? 'Regenerating...' : 'Simple plan'}
          </button>
          <button
            onClick={() => onPlanChange('complex')}
            disabled={isRegenerating}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              isRegenerating
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
            }`}
          >
            {isRegenerating ? 'Regenerating...' : 'Complex plan'}
          </button>
        </div>
      )}
    </div>
  );
};

export default InteractiveSteps;
